/**
 * PCDuplicateGuard — one cart line per variant, store-wide.
 *
 * Why this exists
 * ---------------
 * Nothing in this store is sold by the unit. A diamond is one physical stone,
 * a setting is built for one shopper, and the cart deliberately renders no
 * quantity column (cart page) and no +/- widget (drawer). So a second add of a
 * variant already in the bag does not give the shopper a second item — it
 * silently pushes an existing line to quantity 2, which they can neither see
 * nor undo, and which is paid for twice at checkout.
 *
 * The rule enforced here is the one Shopify uses to merge lines, run backwards:
 * an add that would land on an existing line is refused; an add that would
 * create a NEW line goes through. A different variant of the same product — a
 * different metal, a different size — is a separate line and adds normally.
 * Only the same variant is turned away.
 *
 * Why it intercepts instead of living at the call sites
 * ----------------------------------------------------
 * /cart/add.js is posted from at least eight places: the product form, the
 * quickview modal, "frequently bought together", the upsell popup, the gift
 * automation, and the ring builder's diamond / setting / express flows. They
 * split across two transports — window.fetch (React, inline theme scripts) and
 * jQuery XHR (theme.js, Shopify.addItem) — so the only place the rule can be
 * stated once is at the transport.
 *
 * Load order: after pc-cart-store.js (whose fetch patch this wraps, so its
 * cart-cache bookkeeping keeps running) and before theme.js.
 */
window.PCDuplicateGuard = (function () {
  // Shopify accepts /cart/add, /cart/add.js and /cart/add.json, optionally
  // behind a locale prefix (/en/cart/add.js).
  var ADD_PATH = /\/cart\/add(\.js|\.json)?$/;
  var CART_WRITE_PATH = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;

  var MESSAGE = 'This item is already in your bag.';
  // Free gifts are added and removed by the gift automation, which already
  // reconciles against the cart. A duplicate there is that reconciler racing
  // itself, not a shopper action, so the line is dropped without a message.
  var GIFT_PROPERTY = '_gift';

  var _prevFetch = window.fetch.bind(window);

  // Variant ids currently in the cart, as string keys. The jQuery prefilter is
  // synchronous and cannot await a cart read, so it decides from this snapshot;
  // every cart write the page makes refreshes it (see refreshSnapshot callers).
  var _snapshot = null;

  // ── Cart reads ─────────────────────────────────────────────────────────────

  function readCart(fresh) {
    if (window.PCCartStore) {
      return fresh ? window.PCCartStore.refresh() : window.PCCartStore.get();
    }
    return _prevFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function idsFromCart(cart) {
    var ids = Object.create(null);
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        ids[String(item.variant_id)] = true;
      });
    }
    return ids;
  }

  // `fresh` forces a new /cart.js. Leave it off for routine upkeep: PCCartStore
  // exists to collapse cart reads, and its cache is already kept current by its
  // own write interception, so a cached read is both correct and free. The one
  // place freshness is worth a request is confirming a refusal (see below).
  function refreshSnapshot(fresh) {
    return readCart(!!fresh)
      .then(function (cart) {
        _snapshot = idsFromCart(cart);
        return _snapshot;
      })
      .catch(function () { return _snapshot; });
  }

  // A /cart/change.js or /cart/add.js response IS the new cart, so a write the
  // guard witnesses updates the snapshot without asking for anything.
  function adoptCart(cart) {
    if (cart && Array.isArray(cart.items) && typeof cart.item_count === 'number') {
      _snapshot = idsFromCart(cart);
      return true;
    }
    return false;
  }

  // ── Payload parsing ────────────────────────────────────────────────────────
  //
  // /cart/add.js takes a single item ({id, quantity, properties}) or a batch
  // ({items: [...]}), as JSON or as form-encoded pairs. Anything this cannot
  // confidently read is passed straight through: refusing an add we do not
  // understand would be worse than letting a duplicate past.

  function isGiftItem(item) {
    return !!(item && item.properties && item.properties[GIFT_PROPERTY]);
  }

  function itemsFromJson(parsed) {
    if (!parsed || typeof parsed !== 'object') return null;
    if (Array.isArray(parsed.items)) return parsed.items;
    if (parsed.id !== undefined) return [parsed];
    return null;
  }

  // pairs: [[key, value], ...] from URLSearchParams or FormData. Only the
  // single-item shape is produced by this theme's form posts; a form-encoded
  // batch (items[0][id]=…) is left alone rather than half-understood.
  function itemsFromPairs(pairs) {
    var item = { properties: {} };
    var sawId = false;
    var sawIndexed = false;

    pairs.forEach(function (pair) {
      var key = String(pair[0]);
      var value = pair[1];
      if (key === 'id') {
        item.id = value;
        sawId = true;
      } else if (key === 'quantity') {
        item.quantity = value;
      } else if (key.indexOf('properties[') === 0) {
        item.properties[key.slice(11, -1)] = value;
      } else if (key.indexOf('items[') === 0) {
        sawIndexed = true;
      }
    });

    if (sawIndexed || !sawId) return null;
    return [item];
  }

  // ── The rule ───────────────────────────────────────────────────────────────

  // Splits a payload into what may be added and what would land on a line that
  // already exists.
  //
  // `blocked` is what the shopper is told about: a variant already in the bag,
  // which is the case they can act on. `silentlyDropped` is the same collision
  // arrived at without a shopper decision behind it - a repeat inside one
  // batch (two copies of the same variant merge into one quantity-2 line just
  // the same), or a free gift, whose add and removal the gift automation owns.
  // Both are dropped; only the first is worth a message.
  function partition(items, ids) {
    var keep = [];
    var blocked = [];
    var silentlyDropped = [];
    var seen = Object.create(null);

    items.forEach(function (item) {
      var id = String(item && item.id !== undefined ? item.id : '');
      if (!id) {
        keep.push(item);
        return;
      }
      if (!ids[id] && !seen[id]) {
        seen[id] = true;
        keep.push(item);
      } else if (ids[id] && !isGiftItem(item)) {
        blocked.push(item);
      } else {
        silentlyDropped.push(item);
      }
    });

    return { keep: keep, blocked: blocked, silentlyDropped: silentlyDropped };
  }

  // ── Telling the shopper ────────────────────────────────────────────────────

  var _lastNoticeAt = 0;

  function notify(message) {
    // One message per burst: a batch add can trip the rule on several lines at
    // once, and the shopper needs telling once, not once per line.
    var now = Date.now();
    if (now - _lastNoticeAt < 2500) return;
    _lastNoticeAt = now;

    if (
      window.theme && window.theme.alert &&
      typeof window.theme.alert.new === 'function' &&
      window.jQuery && window.jQuery.growl
    ) {
      window.theme.alert.new('', message, 4000, 'warning');
      return;
    }
    toast(message);
  }

  // Fallback for pages where the theme's growl plugin has not loaded — the
  // guard runs before theme.js, so an add fired early has nothing else to use.
  function toast(message) {
    var el = document.createElement('div');
    el.className = 'pc-duplicate-toast';
    el.setAttribute('role', 'status');
    el.textContent = message;
    el.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:24px',
      'transform:translateX(-50%)',
      'z-index:2147483647',
      'max-width:min(420px, calc(100vw - 32px))',
      'padding:12px 18px',
      'border-radius:4px',
      'background:var(--g-cta-button, #04043F)',
      'color:#ffffff',
      'font-family:var(--g-font-2, Inter, sans-serif)',
      'font-size:14px',
      'line-height:1.4',
      'text-align:center',
      'box-shadow:0 6px 24px rgba(0,0,0,0.18)'
    ].join(';');
    document.body.appendChild(el);
    setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, 4000);
  }

  // The refusal is shaped like a Shopify cart error so existing callers read it
  // the way they already read a real one.
  //
  // `message` is deliberately absent: theme.js's cart error handler shows its
  // generic warning growl only `if (data.message)`, and the guard has already
  // said what happened. `pc_duplicate` lets callers tell a refusal apart from a
  // transient failure — the ring builder's retry wrapper uses it to fail fast
  // rather than retrying five times over ten seconds.
  function refusalResponse() {
    var body = {
      status: 422,
      description: MESSAGE,
      pc_duplicate: true
    };
    return new Response(JSON.stringify(body), {
      status: 422,
      statusText: 'Unprocessable Entity',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // ── fetch ──────────────────────────────────────────────────────────────────

  function pathnameOf(url) {
    try {
      return new URL(String(url), window.location.origin).pathname;
    } catch (e) {
      return String(url).split('?')[0].split('#')[0];
    }
  }

  function readBodyItems(body) {
    if (typeof body === 'string') {
      var trimmed = body.trim();
      if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
        try {
          return { kind: 'json', items: itemsFromJson(JSON.parse(trimmed)) };
        } catch (e) {
          return { kind: null, items: null };
        }
      }
      return {
        kind: 'pairs',
        items: itemsFromPairs(Array.from(new URLSearchParams(body).entries()))
      };
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return { kind: 'pairs', items: itemsFromPairs(Array.from(body.entries())) };
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return { kind: 'formdata', items: itemsFromPairs(Array.from(body.entries())) };
    }
    return { kind: null, items: null };
  }

  window.fetch = function (input, init) {
    var options = init || {};
    var method = String(options.method || 'GET').toUpperCase();

    // A Request object hides its body behind an async read, and nothing in this
    // theme posts one — not worth the complexity, so it passes through.
    if (
      method !== 'POST' ||
      typeof input === 'object' ||
      !ADD_PATH.test(pathnameOf(input)) ||
      options.body === undefined
    ) {
      return _prevFetch(input, options);
    }

    var parsed = readBodyItems(options.body);
    if (!parsed.items || parsed.items.length === 0) {
      return _prevFetch(input, options);
    }

    return readCart(false)
      .then(function (cart) {
        var split = partition(parsed.items, idsFromCart(cart));
        if (split.blocked.length === 0 && split.silentlyDropped.length === 0) {
          return _prevFetch(input, options);
        }

        // The cached cart said duplicate. Confirm against a fresh read before
        // refusing: a stale cache turning a legitimate add away is a worse
        // failure than the duplicate this guards against.
        return readCart(true).then(function (freshCart) {
          _snapshot = idsFromCart(freshCart);
          var confirmed = partition(parsed.items, _snapshot);

          if (confirmed.blocked.length === 0 && confirmed.silentlyDropped.length === 0) {
            return _prevFetch(input, options);
          }
          if (confirmed.blocked.length > 0) notify(MESSAGE);

          // Nothing left worth sending, or a single-item shape that is
          // all-or-nothing by construction.
          if (confirmed.keep.length === 0 || parsed.kind !== 'json') {
            return refusalResponse();
          }

          // Part of the batch is new — send that part rather than losing the
          // whole add (a ring whose matching band is already in the bag still
          // needs its setting adding).
          var rewritten = Object.assign({}, options, {
            body: JSON.stringify({ items: confirmed.keep })
          });
          return _prevFetch(input, rewritten);
        });
      })
      .catch(function () {
        // Never let a failure inside the guard stop a shopper adding to cart.
        return _prevFetch(input, options);
      });
  };

  // ── jQuery ─────────────────────────────────────────────────────────────────
  //
  // theme.js's adds go out on jQuery XHR, and a prefilter has to answer
  // synchronously — so these decide from the snapshot rather than a live read.
  // Every cart write the page makes refreshes it, so it only goes stale if the
  // cart is changed in another tab; a refusal kicks off a refresh, so the next
  // attempt is decided on current data either way.

  function installJqueryGuard($) {
    $.ajaxPrefilter(function (options, originalOptions, jqXHR) {
      if (!ADD_PATH.test(pathnameOf(options.url))) return;
      if (!_snapshot) return;

      // Prefilters run before jQuery serialises `data`, so the original object
      // is still available for a batch add.
      var data = originalOptions ? originalOptions.data : options.data;
      var items = null;

      if (typeof data === 'string') {
        items = itemsFromPairs(Array.from(new URLSearchParams(data).entries()));
      } else if (data && typeof data === 'object') {
        items = Array.isArray(data.items)
          ? data.items
          : (data.id !== undefined ? [data] : null);
      }
      if (!items || items.length === 0) return;

      var split = partition(items, _snapshot);
      if (split.blocked.length === 0 && split.silentlyDropped.length === 0) return;

      if (split.keep.length > 0 && data && Array.isArray(data.items)) {
        // Batch add (frequently-bought-together): drop the duplicates and let
        // the rest through.
        options.data = Object.assign({}, data, { items: split.keep });
        if (split.blocked.length > 0) notify(MESSAGE);
        return;
      }

      if (split.blocked.length > 0) notify(MESSAGE);
      // Synchronous verdicts are only as good as the snapshot. If this one was
      // wrong - the cart changed in another tab - a fresh read means the next
      // attempt is decided on current data.
      refreshSnapshot(true);
      jqXHR.abort();
    });

    // Covers Shopify.addItem / Shopify.changeItem and the theme's own posts,
    // none of which go through window.fetch.
    $(document).ajaxComplete(function (event, xhr, settings) {
      if (!settings || !CART_WRITE_PATH.test(pathnameOf(settings.url))) return;
      // /cart/change.js and /cart/update.js answer with the whole cart. Adopt
      // it directly: theme.js does hand these to PCCartStore, but not reliably
      // before this fires, so a cached re-read here can still be a step behind.
      if (adoptCart(xhr && xhr.responseJSON)) return;
      if (window.PCCartStore) window.PCCartStore.invalidate();
      refreshSnapshot();
    });
  }

  // ── Native form posts ──────────────────────────────────────────────────────
  //
  // A <form action="/cart/add"> submitted normally is a full page navigation:
  // no fetch, no XHR, nothing either interceptor above can see. The cart page's
  // matching-band upsell is one of these. Re-issuing the same add through our
  // own fetch puts it back under the rule, and lands the shopper on /cart just
  // as the native post would have.
  //
  // Bubble phase, NOT capture, and the defaultPrevented check is what makes it
  // safe. theme.js's AjaxCart binds a submit handler directly to the product
  // forms (#AddToCartForm-*, product cards) that calls preventDefault and posts
  // the add over jQuery - already covered by the prefilter above. A listener on
  // document in the bubble phase runs after those, so defaultPrevented is
  // already true and this stands down. From capture it would run FIRST, add the
  // item itself, and then let jQuery add it a second time.
  function installFormGuard() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM' || event.defaultPrevented) return;
      if (!ADD_PATH.test(pathnameOf(form.getAttribute('action') || ''))) return;
      // Shop Pay's payment-terms markup is a /cart/add form that exists only to
      // host the widget and is never submitted for real. Leave anything with a
      // payment element in it alone rather than racing Shopify's own scripts.
      if (form.querySelector('shopify-payment-terms, [data-shopify="payment-button"]')) return;

      event.preventDefault();
      var body = new URLSearchParams();
      new FormData(form).forEach(function (value, key) {
        // FormData carries File entries for any file input; /cart/add.js wants
        // scalars, and none of this theme's add forms have one.
        if (typeof value === 'string') body.append(key, value);
      });

      window.fetch(withRoot('cart/add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json'
        },
        body: body.toString()
      })
        .then(function (res) {
          // A refusal has already been explained by notify(); only a real add
          // should move the shopper to the cart.
          if (res.ok) window.location.href = withRoot('cart');
        })
        .catch(function () {
          // Guard failure must not swallow the shopper's click - let the form
          // go through the way it always did. form.submit() fires no submit
          // event, so this cannot loop back into this handler.
          form.submit();
        });
    }, false);
  }

  // Keeps the locale prefix the page is already on (/en/cart/add.js).
  function withRoot(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }

  // ── Boot ───────────────────────────────────────────────────────────────────

  // Seeded synchronously from the page's own Liquid-rendered cart (see
  // snippets/header-js.liquid) so the jQuery prefilter - which cannot await a
  // cart read - has a correct answer for a click that lands before the async
  // read below returns. Without it the very first "Add to cart" on a page was
  // waved through.
  if (Array.isArray(window.PC_CART_VARIANT_IDS)) {
    _snapshot = Object.create(null);
    window.PC_CART_VARIANT_IDS.forEach(function (id) {
      _snapshot[String(id)] = true;
    });
  }

  refreshSnapshot();
  installFormGuard();

  if (window.jQuery) {
    installJqueryGuard(window.jQuery);
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      if (window.jQuery) installJqueryGuard(window.jQuery);
    });
  }

  // A bfcache restore brings back a page whose snapshot predates whatever the
  // shopper did at checkout or in another tab.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshSnapshot(true);
  });

  return {
    message: MESSAGE,
    refresh: refreshSnapshot,
    // Exposed for debugging: which variant ids the guard believes are in the bag.
    snapshot: function () { return _snapshot; }
  };
})();
